// BYO-01 "My subscriptions" › Channels: the channels the caller has joined, with the notification
// level, mute and leave of each (M2-02 `channels.subscription` / `channels.leave`), and "Browse
// channels" to join the public ones not joined yet (`channels.join`; joining grants nothing).
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  EmptyState,
  HomeCard,
  HomeCardRow,
  NativeSelect,
  NativeSelectOption,
  Notice,
  Switch,
  toast,
} from "@companyos/ui";
import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { HashIcon, LockIcon, MessageSquareIcon } from "lucide-react";
import { useState } from "react";
import type { Api } from "../../api/client";
import { LoadError, Loading, LoadMore } from "../screen-parts";
import { problemText } from "../settings/problem-text";
import { useFeature } from "../use-feature";
import { channelsQuery, type SidebarChannel, subscriptionKeys } from "./queries";

const LEVELS = [
  { value: "all", label: "Every message" },
  { value: "mentions", label: "Mentions only" },
  { value: "none", label: "Nothing" },
] as const;

export function ChannelsTab({ api, co }: { api: Api; co: string }) {
  const chat = useFeature("chat");
  if (!chat.enabled) {
    return (
      <Notice tone="info" title="Chat is off for this company">
        Channel subscriptions appear here once an admin turns on Chat in Setup › Add-ons.
      </Notice>
    );
  }
  return <Joined api={api} co={co} />;
}

function Joined({ api, co }: { api: Api; co: string }) {
  const qc = useQueryClient();
  const q = useInfiniteQuery(channelsQuery(api, co, true));
  const [browsing, setBrowsing] = useState(false);
  const rows = (q.data?.pages.flatMap((p) => p.data) ?? []).filter((c) => !c.dm_key);
  const refresh = () => void qc.invalidateQueries({ queryKey: subscriptionKeys.all(co) });
  const patch = useMutation({
    mutationFn: (x: {
      c: SidebarChannel;
      notify_level?: SidebarChannel["notify_level"];
      muted?: boolean;
    }) =>
      api.client.channels.subscription({
        params: { co, id: x.c.id },
        body: {
          ...(x.notify_level ? { notify_level: x.notify_level } : {}),
          ...(x.muted !== undefined ? { muted: x.muted } : {}),
        },
      }),
    onSuccess: refresh,
    onError: (e) => toast.add({ title: problemText(e, "Not saved."), type: "error" }),
  });
  const leave = useMutation({
    mutationFn: (c: SidebarChannel) => api.client.channels.leave({ params: { co, id: c.id } }),
    onSuccess: (_v, c) => {
      refresh();
      toast.add({ title: `Left #${c.name}`, type: "success" });
    },
    onError: (e) => toast.add({ title: problemText(e, "You are still in it."), type: "error" }),
  });
  return (
    <div className="flex flex-col gap-4">
      <HomeCard
        title="Joined channels"
        count={rows.length}
        flush
        action={
          <Button size="sm" variant="secondary" onClick={() => setBrowsing(true)}>
            Browse channels
          </Button>
        }
      >
        {q.isPending ? (
          <div className="p-4">
            <Loading label="Loading channels" rows={3} />
          </div>
        ) : q.isError ? (
          <div className="p-4">
            <LoadError what="Channels" error={q.error} />
          </div>
        ) : rows.length === 0 ? (
          <EmptyState
            icon={MessageSquareIcon}
            title="No channels joined"
            description="Browse the channels you can see and join the ones you want to follow."
          />
        ) : (
          rows.map((c) => (
            <HomeCardRow
              key={c.id}
              data-channel={c.id}
              lead={
                c.is_private ? <LockIcon aria-hidden="true" /> : <HashIcon aria-hidden="true" />
              }
              title={<span className="font-medium">{c.name}</span>}
              description={
                <span className="flex flex-wrap items-center gap-2">
                  <NativeSelect
                    size="sm"
                    aria-label={`Notifications for #${c.name}`}
                    value={c.notify_level ?? "all"}
                    disabled={patch.isPending}
                    onChange={(e) =>
                      patch.mutate({
                        c,
                        notify_level: e.target.value as SidebarChannel["notify_level"],
                      })
                    }
                  >
                    {LEVELS.map((l) => (
                      <NativeSelectOption key={l.value} value={l.value}>
                        {l.label}
                      </NativeSelectOption>
                    ))}
                  </NativeSelect>
                  <span className="flex items-center gap-2">
                    <Switch
                      aria-label={`Mute #${c.name}`}
                      checked={c.muted}
                      disabled={patch.isPending}
                      onCheckedChange={(v) => patch.mutate({ c, muted: v === true })}
                    />
                    <span className="text-xs">Muted</span>
                  </span>
                  {c.is_agent_room ? <Badge variant="info">Agent room</Badge> : null}
                </span>
              }
              trailing={
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={leave.isPending}
                  onClick={() => leave.mutate(c)}
                >
                  Leave
                </Button>
              }
            />
          ))
        )}
      </HomeCard>
      <LoadMore
        what="channels"
        hasMore={q.hasNextPage}
        loading={q.isFetchingNextPage}
        onMore={() => void q.fetchNextPage()}
      />
      {browsing ? <Browse api={api} co={co} onClose={() => setBrowsing(false)} /> : null}
    </div>
  );
}

function Browse({ api, co, onClose }: { api: Api; co: string; onClose: () => void }) {
  const qc = useQueryClient();
  const q = useInfiniteQuery(channelsQuery(api, co, false));
  const rows = (q.data?.pages.flatMap((p) => p.data) ?? []).filter(
    (c) => !c.joined && !c.is_private && !c.dm_key && !c.archived_at,
  );
  const join = useMutation({
    mutationFn: (c: SidebarChannel) => api.client.channels.join({ params: { co, id: c.id } }),
    onSuccess: (_v, c) => {
      void qc.invalidateQueries({ queryKey: subscriptionKeys.all(co) });
      toast.add({ title: `Joined #${c.name}`, type: "success" });
    },
    onError: (e) => toast.add({ title: problemText(e, "Not joined."), type: "error" }),
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Browse channels</DialogTitle>
          <DialogDescription>
            Public channels you can see and have not joined. Joining only subscribes you.
          </DialogDescription>
        </DialogHeader>
        {q.isPending ? (
          <Loading label="Loading channels" rows={3} />
        ) : q.isError ? (
          <LoadError what="Channels" error={q.error} />
        ) : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">You are in every channel you can join.</p>
        ) : (
          <ul className="flex max-h-[60vh] flex-col overflow-y-auto">
            {rows.map((c) => (
              <li
                key={c.id}
                className="flex items-center justify-between gap-3 border-t border-subtle py-2 first:border-t-0"
              >
                <span className="flex min-w-0 items-center gap-2 text-sm">
                  <HashIcon aria-hidden="true" className="size-4 text-muted-foreground" />
                  <span className="truncate">{c.name}</span>
                </span>
                <Button size="sm" disabled={join.isPending} onClick={() => join.mutate(c)}>
                  Join
                </Button>
              </li>
            ))}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
}
